import { useState } from 'react';
import type { Alert } from '../lib/types';
import { fmtAgo } from '../lib/format';
import { Icon } from './HeartMark';
import { Card } from './Bits';

/** How many are shown before the list asks to be opened. */
export const ALERTS_SHOWN = 3;

const RANK: Record<Alert['level'], number> = { urgent: 0, attention: 1, info: 2 };

/**
 * Worst first, and within a level the order they arrived in — the practice sends them unacked-first
 * and newest-first, which is the right tiebreak.
 *
 * This ordering is what makes it safe to collapse the list at all. The rows arrive by time, so a
 * dashboard showing the first three would put an urgent alert below the fold the moment three
 * newer attention alerts landed. Whatever is hidden here is always the least severe thing open.
 */
export function rankAlerts(alerts: readonly Alert[]): Alert[] {
  return alerts.map((a, i) => ({ a, i })).sort((x, y) => RANK[x.a.level] - RANK[y.a.level] || x.i - y.i).map(({ a }) => a);
}

/**
 * The open alerts, collapsed to the first few. A care team opens this page to find who needs them
 * now; twenty rows of "missed a dose" is how the one that matters gets scrolled past.
 */
export function AlertList({ alerts, patientHref, onAcknowledge }: { alerts: readonly Alert[]; patientHref: (patientId: string) => string; onAcknowledge: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  if (alerts.length === 0) return null;

  const ranked = rankAlerts(alerts);
  const shown = open ? ranked : ranked.slice(0, ALERTS_SHOWN);
  const hidden = ranked.length - shown.length;
  const urgent = ranked.filter((a) => a.level === 'urgent').length;

  return (
    <Card>
      <div className="card-head">
        <h2>Open alerts</h2>
        <span className="hint">
          {ranked.length} open{urgent > 0 ? ` · ${urgent} urgent` : ''}
        </span>
      </div>
      <ul className="alerts">
        {shown.map((a) => (
          <li key={a.id} className={`alert alert-${a.level}`}>
            <Icon name="alert" size={18} />
            <span className="row-main">
              <strong>
                <a href={patientHref(a.patientId)}>{a.patientName}</a> · {a.level}
              </strong>
              <span>{a.text}</span>
              <span className="hint">{fmtAgo(a.at)}</span>
            </span>
            <button type="button" className="quiet small" onClick={() => onAcknowledge(a.id)}>
              Acknowledge
            </button>
          </li>
        ))}
      </ul>
      {hidden > 0 || open ? (
        <button type="button" className="quiet small alerts-more" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? `Show fewer` : `Show ${hidden} more`}
        </button>
      ) : null}
    </Card>
  );
}
